import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { emisionDteApi, clientesApi } from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../components/ui/dialog';
import { TableSkeleton } from '../../lib/skeletons';
import { fmtFecha } from '../../lib/utils';
import { FileText, Plus, RefreshCw, Loader2, Search, Receipt, Ticket, AlertTriangle, Eye, Factory, Cloud, CloudDownload, CheckCircle2, XCircle, ShieldAlert, Trash2, RotateCcw, CornerDownRight } from 'lucide-react';
import { toast } from 'sonner';
import { SearchableCombobox } from '../../components/ui/searchable-combobox';
import { EstadoBadge, SituacionBadge, EstadoContableBadge, OrigenBadge, TIPO_LABEL, TIPO_COLOR, TipoBadge } from './emisionBadges';

const fmt = (n) => (typeof n === 'number' ? n.toLocaleString('es-CL', { maximumFractionDigits: 0 }) : (n || '0'));

const ESTADOS_FILTRO = [
  { key: 'all', label: 'Todos', params: {} },
  { key: 'borrador', label: 'Borrador', params: { emitido: false } },
  { key: 'emitidos', label: 'Emitidos', params: { emitido: true } },
  { key: 'pend_contab', label: 'Pend. contab.', params: { emitido: true, estado_contable: 'pendiente' } },
  { key: 'contabilizados', label: 'Contabilizados', params: { estado_contable: 'contabilizado' } },
  { key: 'cedidos', label: 'Cedidos', params: { situacion: 'cedida' } },
  { key: 'papelera', label: 'Papelera', params: { papelera: true } },
];

const TIPOS_FILTRO = [
  { key: 'all', label: 'Todos', tipo: null },
  { key: '33', label: 'Factura Afecta', tipo: '33' },
  { key: '34', label: 'Factura Exenta', tipo: '34' },
  { key: '61', label: 'Nota de Crédito', tipo: '61' },
  { key: '56', label: 'Nota de Débito', tipo: '56' },
  { key: '52', label: 'Guías de Despacho', tipo: '52' },
];

export default function EmisionDTEList() {
  const navigate = useNavigate();
  const [docs, setDocs] = useState([]);
  const [total, setTotal] = useState(0);
  const [resumen, setResumen] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filtroKey, setFiltroKey] = useState('all');
  const [tipoKey, setTipoKey] = useState('all');
  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');
  const [q, setQ] = useState('');
  const [folios, setFolios] = useState([]);

  // Selección / limpieza de borradores
  const [selected, setSelected] = useState(new Set());
  const [limpiarOpen, setLimpiarOpen] = useState(false);
  const [limpiando, setLimpiando] = useState(false);

  // Nuevo documento
  const [nuevoOpen, setNuevoOpen] = useState(false);
  const [clientes, setClientes] = useState([]);
  const [nuevoForm, setNuevoForm] = useState({ tipo_dte: '33', cliente_rut: '', orden_compra: '' });
  const [creando, setCreando] = useState(false);
  const [modo, setModo] = useState('nuevo'); // 'nuevo' | 'proforma'
  const [proformas, setProformas] = useState([]);
  const [proformaId, setProformaId] = useState('');

  // Documentos externos (emitidos directo en DTECore)
  const [externoOpen, setExternoOpen] = useState(false);
  const [externoForm, setExternoForm] = useState({ tipo_dte: '33', folio: '' });
  const [traendo, setTraendo] = useState(false);
  const [sincronizando, setSincronizando] = useState(false);

  // Folios
  const [foliosOpen, setFoliosOpen] = useState(false);
  const [foliosForm, setFoliosForm] = useState({});
  const [savingFolios, setSavingFolios] = useState(false);

  // Conexión con el proveedor
  const [config, setConfig] = useState(null);
  const [cfgOpen, setCfgOpen] = useState(false);
  const [cfgForm, setCfgForm] = useState({ modo: 'registro', confirmar_produccion: false });
  const [savingCfg, setSavingCfg] = useState(false);
  const [probando, setProbando] = useState(false);
  const [probeResult, setProbeResult] = useState(null);

  const loadDocs = useCallback(async () => {
    setLoading(true);
    try {
      const f = ESTADOS_FILTRO.find((x) => x.key === filtroKey) || ESTADOS_FILTRO[0];
      const t = TIPOS_FILTRO.find((x) => x.key === tipoKey);
      const res = await emisionDteApi.list({
        ...f.params,
        tipo_dte: (t && t.tipo) || undefined,
        desde: desde || undefined,
        hasta: hasta || undefined,
        q: q || undefined,
        limit: 100,
      });
      setDocs(res.data.documentos || []);
      setTotal(res.data.total || 0);
      setResumen(res.data.resumen || null);
      setSelected(new Set());
    } catch {
      toast.error('Error al cargar los documentos');
    } finally {
      setLoading(false);
    }
  }, [filtroKey, tipoKey, desde, hasta, q]);

  const loadFolios = useCallback(async () => {
    try {
      const res = await emisionDteApi.folios();
      setFolios(res.data.folios || []);
    } catch { /* noop */ }
  }, []);

  const loadConfig = useCallback(async () => {
    try {
      const res = await emisionDteApi.getConfig();
      setConfig(res.data);
    } catch { /* noop */ }
  }, []);

  useEffect(() => { loadDocs(); }, [loadDocs]);
  useEffect(() => { loadFolios(); loadConfig(); }, [loadFolios, loadConfig]);

  const traerExterno = async () => {
    const folio = parseInt(externoForm.folio, 10);
    if (!folio) { toast.error('Ingresa el folio del documento'); return; }
    setTraendo(true);
    try {
      const { data } = await emisionDteApi.traerExterno({ tipo_dte: externoForm.tipo_dte, folio });
      toast.success(data.creado ? `Documento folio ${folio} traído al ERP` : 'El documento ya estaba en el ERP');
      setExternoOpen(false);
      setExternoForm({ tipo_dte: '33', folio: '' });
      await loadDocs();
      if (data?.documento?.id) navigate(`/emision-dte/${data.documento.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'No se pudo traer el documento desde DTECore');
    } finally { setTraendo(false); }
  };

  const sincronizarExternos = async () => {
    setSincronizando(true);
    try {
      const { data } = await emisionDteApi.syncExternos();
      const n = (data?.creados || []).length;
      toast.success(n ? `${n} documento(s) externo(s) incorporado(s)` : 'No hay documentos externos nuevos');
      await loadDocs();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'No se pudo sincronizar con DTECore');
    } finally { setSincronizando(false); }
  };

  const isPapelera = filtroKey === 'papelera';
  const draftIds = docs.filter((d) => !d.emitido).map((d) => d.id);
  const allSelected = draftIds.length > 0 && draftIds.every((id) => selected.has(id));
  const toggleOne = (id) => setSelected((prev) => {
    const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n;
  });
  const toggleAll = () => setSelected(() => (allSelected ? new Set() : new Set(draftIds)));

  const confirmarLimpiar = async () => {
    setLimpiando(true);
    try {
      const r = await emisionDteApi.limpiar([...selected]);
      toast.success(`${r.data.descartados} borrador(es) enviados a la papelera · recuperables ${r.data.dias_recuperacion} días`);
      setSelected(new Set());
      setLimpiarOpen(false);
      loadDocs();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'No se pudieron limpiar los borradores');
    } finally {
      setLimpiando(false);
    }
  };

  const restaurar = async (id) => {
    try {
      await emisionDteApi.restaurar(id);
      toast.success('Borrador restaurado');
      loadDocs();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'No se pudo restaurar');
    }
  };

  const openConfig = () => {
    setCfgForm({
      modo: config?.modo || 'registro',
      emisor_rut: config?.emisor_rut || '',
      sucursal_nombre: config?.sucursal_nombre || 'CASA MATRIZ',
      sucursal_codigo: config?.sucursal_codigo || '1',
      confirmar_produccion: false,
    });
    setProbeResult(null);
    setCfgOpen(true);
  };

  const probarConexion = async () => {
    setProbando(true);
    setProbeResult(null);
    try {
      const res = await emisionDteApi.probarConexion();
      setProbeResult(res.data);
    } catch (err) {
      setProbeResult({ ok: false, mensaje: err?.response?.data?.detail || 'No se pudo probar la conexión' });
    } finally {
      setProbando(false);
    }
  };

  const guardarConfig = async () => {
    setSavingCfg(true);
    try {
      const res = await emisionDteApi.setConfig({
        modo: cfgForm.modo,
        emisor_rut: cfgForm.emisor_rut || undefined,
        sucursal_nombre: cfgForm.sucursal_nombre,
        sucursal_codigo: cfgForm.sucursal_codigo,
        confirmar_produccion: cfgForm.confirmar_produccion,
      });
      setConfig(res.data);
      toast.success('Conexión actualizada');
      setCfgOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'No se pudo guardar la configuración');
    } finally {
      setSavingCfg(false);
    }
  };

  const openNuevo = async () => {
    setNuevoForm({ tipo_dte: '33', cliente_rut: '', orden_compra: '' });
    setModo('nuevo');
    setProformaId('');
    setNuevoOpen(true);
    if (clientes.length === 0) {
      try { const r = await clientesApi.getAll(); setClientes(r.data || []); } catch { /* noop */ }
    }
    try {
      const rp = await api.get('/facturacion/proformas', { params: { estado: 'aprobada' } });
      setProformas(rp.data?.proformas || []);
    } catch { /* noop */ }
  };

  const crearDoc = async () => {
    setCreando(true);
    try {
      const body = modo === 'proforma'
        ? { tipo_dte: nuevoForm.tipo_dte, proforma_id: proformaId, items: [], referencias: {} }
        : {
          tipo_dte: nuevoForm.tipo_dte,
          cliente_rut: nuevoForm.cliente_rut,
          items: [],
          referencias: { orden_compra: nuevoForm.orden_compra || '' },
        };
      const res = await emisionDteApi.crear(body);
      toast.success('Borrador creado');
      setNuevoOpen(false);
      navigate(`/emision-dte/${res.data.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'No se pudo crear el documento');
    } finally {
      setCreando(false);
    }
  };

  const openFolios = () => {
    const form = {};
    folios.forEach((f) => { form[f.tipo_dte] = { disponibles: f.disponibles || 0, umbral_alerta: f.umbral_alerta ?? 20, proximo_folio: f.proximo_folio || '', bloqueo_sin_folios: f.bloqueo_sin_folios !== false }; });
    setFoliosForm(form);
    setFoliosOpen(true);
  };

  const guardarFolios = async () => {
    setSavingFolios(true);
    try {
      for (const tipo of Object.keys(foliosForm)) {
        const f = foliosForm[tipo];
        await emisionDteApi.setFolios(tipo, {
          disponibles: Number(f.disponibles) || 0,
          umbral_alerta: Number(f.umbral_alerta) || 0,
          bloqueo_sin_folios: !!f.bloqueo_sin_folios,
          proximo_folio: f.proximo_folio ? Number(f.proximo_folio) : null,
        });
      }
      toast.success('Folios actualizados');
      setFoliosOpen(false);
      loadFolios();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al guardar folios');
    } finally {
      setSavingFolios(false);
    }
  };

  return (
    <div className="space-y-4 pb-20" data-testid="emision-dte-list">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Factory className="w-6 h-6 text-indigo-600" /> Emisión de DTE
          </h1>
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            <p className="text-sm text-muted-foreground">{total} documento(s)</p>
            {config && <ModoBadge config={config} />}
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" size="sm" onClick={() => { loadDocs(); loadFolios(); loadConfig(); }}>
            <RefreshCw className="w-4 h-4 mr-1" /> Actualizar
          </Button>
          {config?.puede_configurar && (
            <Button variant="outline" size="sm" onClick={openConfig} data-testid="btn-conexion" className="text-purple-700 border-purple-300 hover:bg-purple-50">
              <Cloud className="w-4 h-4 mr-1" /> Conexión proveedor
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={openFolios} data-testid="btn-folios" className="text-blue-700 border-blue-300 hover:bg-blue-50">
            <Ticket className="w-4 h-4 mr-1" /> Folios (CAF)
          </Button>
          {config?.puede_configurar && (
            <>
              <Button variant="outline" size="sm" onClick={() => setExternoOpen(true)} data-testid="btn-traer-externo" className="text-amber-700 border-amber-300 hover:bg-amber-50">
                <CloudDownload className="w-4 h-4 mr-1" /> Traer externo
              </Button>
              <Button variant="outline" size="sm" onClick={sincronizarExternos} disabled={sincronizando} data-testid="btn-sync-externos" className="text-amber-700 border-amber-300 hover:bg-amber-50">
                {sincronizando ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />} Sincronizar externos
              </Button>
            </>
          )}
          <Button size="sm" onClick={openNuevo} data-testid="btn-nuevo-documento" className="bg-indigo-600 hover:bg-indigo-700">
            <Plus className="w-4 h-4 mr-1" /> Nuevo documento
          </Button>
        </div>
      </div>

      {/* Folios panel */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="folios-panel">
        {folios.map((f) => (
          <div key={f.tipo_dte} className={`rounded-lg border p-3 ${f.bloqueado ? 'bg-red-50/60 border-red-200' : f.alerta ? 'bg-amber-50/60 border-amber-200' : 'bg-white'}`}>
            <div className="flex items-center justify-between">
              <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{f.tipo_dte_nombre}</span>
              {f.bloqueado ? <span className="text-[10px] font-bold text-red-600 flex items-center gap-1"><AlertTriangle className="w-3 h-3" />Sin folios</span>
                : f.alerta ? <span className="text-[10px] font-bold text-amber-700 flex items-center gap-1"><AlertTriangle className="w-3 h-3" />Por agotarse</span> : null}
            </div>
            <p className={`text-2xl font-bold ${f.bloqueado ? 'text-red-600' : f.alerta ? 'text-amber-700' : 'text-gray-900'}`}>{f.disponibles}</p>
            <p className="text-[10px] text-muted-foreground">folios disponibles{f.proximo_folio ? ` · próximo ${f.proximo_folio}` : ''}</p>
          </div>
        ))}
      </div>

      {/* Filtros */}
      <Card>
        <CardContent className="p-3 space-y-3">
          <div className="flex flex-col sm:flex-row gap-3">
            <form onSubmit={(e) => { e.preventDefault(); loadDocs(); }} className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input data-testid="search-emision" placeholder="Buscar cliente, RUT, folio, OC…" className="pl-9" value={q} onChange={(e) => setQ(e.target.value)} />
            </form>
            <div className="flex items-center gap-1.5 flex-wrap">
              {ESTADOS_FILTRO.map((e) => (
                <button key={e.key} data-testid={`filter-${e.key}`} onClick={() => setFiltroKey(e.key)}
                  className={`text-xs px-3 py-1.5 rounded-full border font-medium transition-all ${filtroKey === e.key ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white border-gray-200 hover:bg-indigo-50'}`}>
                  {e.label}
                </button>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-1.5 flex-wrap border-t pt-3" data-testid="tipo-filtros">
            <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mr-1">Tipo</span>
            {TIPOS_FILTRO.map((t) => {
              const c = TIPO_COLOR[t.tipo];
              const active = tipoKey === t.key;
              return (
                <button key={t.key} data-testid={`tipo-filter-${t.key}`} onClick={() => setTipoKey(t.key)}
                  className={`text-xs px-3 py-1.5 rounded-full border font-medium transition-all inline-flex items-center gap-1.5 ${active ? (c ? c.active : 'bg-indigo-600 text-white border-indigo-600') : (c ? `bg-white ${c.text} ${c.border} hover:bg-gray-50` : 'bg-white border-gray-200 hover:bg-indigo-50')}`}>
                  {c && <span className={`w-2 h-2 rounded-full ${active ? 'bg-white' : c.dot}`} />}
                  {t.label}
                </button>
              );
            })}
          </div>
          <div className="flex items-center gap-2 flex-wrap border-t pt-3" data-testid="rango-fecha-filtros">
            <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mr-1">Emisión</span>
            <div className="flex items-center gap-1.5">
              <Label className="text-[11px] text-muted-foreground">Desde</Label>
              <Input type="date" value={desde} onChange={(e) => setDesde(e.target.value)} data-testid="filtro-desde" className="h-8 w-[150px] text-xs" />
            </div>
            <div className="flex items-center gap-1.5">
              <Label className="text-[11px] text-muted-foreground">Hasta</Label>
              <Input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} data-testid="filtro-hasta" className="h-8 w-[150px] text-xs" />
            </div>
            {(desde || hasta) && (
              <Button variant="ghost" size="sm" className="h-8 text-xs text-muted-foreground" onClick={() => { setDesde(''); setHasta(''); }} data-testid="filtro-fecha-limpiar">
                <XCircle className="w-3.5 h-3.5 mr-1" /> Limpiar fechas
              </Button>
            )}
            {(desde || hasta) && <span className="text-[11px] text-amber-600">Se ocultan los borradores sin emitir.</span>}
          </div>
        </CardContent>
      </Card>

      {/* Barra de selección para limpiar borradores */}
      {selected.size > 0 && !isPapelera && (
        <div className="flex items-center justify-between gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-2.5" data-testid="barra-seleccion">
          <span className="text-sm font-medium text-red-800">{selected.size} borrador(es) seleccionado(s)</span>
          <div className="flex gap-2">
            <Button variant="ghost" size="sm" onClick={() => setSelected(new Set())} data-testid="btn-cancelar-seleccion">Cancelar</Button>
            <Button size="sm" className="bg-red-600 hover:bg-red-700" onClick={() => setLimpiarOpen(true)} data-testid="btn-limpiar-borradores">
              <Trash2 className="w-4 h-4 mr-1" /> Limpiar seleccionados
            </Button>
          </div>
        </div>
      )}

      {isPapelera && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm text-amber-800 flex items-center gap-2" data-testid="aviso-papelera">
          <AlertTriangle className="w-4 h-4 shrink-0" />
          Los borradores descartados se pueden restaurar aquí. Pasados 5 días se eliminan automáticamente.
        </div>
      )}

      {/* Total neto del conjunto filtrado */}
      {resumen && !isPapelera && (
        <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-1 rounded-lg border border-indigo-200 bg-indigo-50/60 px-4 py-2.5" data-testid="total-neto-bar">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-muted-foreground">
            <span>Facturas + ND: <span className="font-mono font-semibold text-emerald-700">${fmt(resumen.ventas)}</span></span>
            <span className="text-gray-400">−</span>
            <span>Notas de Crédito: <span className="font-mono font-semibold text-red-600">${fmt(resumen.notas_credito)}</span></span>
            <span className="text-gray-400 hidden sm:inline">· Guías de Despacho no suman</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold uppercase tracking-wider text-indigo-700">Total neto</span>
            <span className="font-mono text-lg font-bold text-indigo-900" data-testid="total-neto-valor">${fmt(resumen.neto)}</span>
          </div>
        </div>
      )}

      {/* Tabla */}
      <Card>
        <CardContent className="p-0">
          {loading ? <TableSkeleton rows={8} cols={7} /> : docs.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <FileText className="w-10 h-10 mx-auto mb-3 opacity-30" />
              <p className="font-medium">Sin documentos</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="emision-table">
                <thead className="bg-muted/50 border-b">
                  <tr>
                    {!isPapelera && (
                      <th className="px-3 py-2.5 w-8 text-center">
                        <input type="checkbox" checked={allSelected} onChange={toggleAll} disabled={draftIds.length === 0}
                          aria-label="Seleccionar todos los borradores" data-testid="select-all-borradores" />
                      </th>
                    )}
                    <th className="px-3 py-2.5 text-left font-semibold">Tipo</th>
                    <th className="px-3 py-2.5 text-center font-semibold">Origen</th>
                    <th className="px-3 py-2.5 text-left font-semibold">Folio</th>
                    <th className="px-3 py-2.5 text-left font-semibold">Cliente</th>
                    <th className="px-3 py-2.5 text-right font-semibold">Total</th>
                    <th className="px-3 py-2.5 text-center font-semibold">Estado</th>
                    <th className="px-3 py-2.5 text-center font-semibold">Situación</th>
                    <th className="px-3 py-2.5 text-left font-semibold">{isPapelera ? 'Descartado' : 'Creado'}</th>
                    <th className="px-3 py-2.5 text-center font-semibold">Acción</th>
                  </tr>
                </thead>
                <tbody>
                  {docs.map((d) => (
                    <tr key={d.id} className={`border-b hover:bg-muted/30 ${isPapelera ? '' : 'cursor-pointer'} ${d.ligada ? 'bg-red-50 border-l-4 border-l-red-500' : ''}`} data-testid={`emision-row-${d.id}`} onClick={() => { if (!isPapelera) navigate(`/emision-dte/${d.id}`); }}>
                      {!isPapelera && (
                        <td className="px-3 py-2 text-center" onClick={(e) => e.stopPropagation()}>
                          {!d.emitido ? (
                            <input type="checkbox" checked={selected.has(d.id)} onChange={() => toggleOne(d.id)}
                              aria-label="Seleccionar borrador" data-testid={`select-borrador-${d.id}`} />
                          ) : null}
                        </td>
                      )}
                      <td className="px-3 py-2"><span className="inline-flex items-center gap-1.5">{d.ligada && <CornerDownRight className="w-3.5 h-3.5 text-red-400" />}<TipoBadge tipo={d.tipo_dte} />{d.tiene_nc && <span className="text-[9px] font-semibold text-red-500 uppercase">· c/NC</span>}</span></td>
                      <td className="px-3 py-2 text-center"><OrigenBadge origen={d.origen} /></td>
                      <td className="px-3 py-2 font-mono font-semibold text-indigo-700">
                        {d.folio || '—'}
                        {d.ligada && d.ref_folio && <div className="text-[10px] font-semibold text-red-600" data-testid={`ref-folio-${d.id}`}>ref. {d.ref_folio}</div>}
                      </td>
                      <td className="px-3 py-2">
                        <div className="truncate max-w-[220px] font-medium">{d.receptor?.razon_social || '—'}</div>
                        <span className="text-[10px] text-muted-foreground">{d.receptor?.rut || ''}</span>
                      </td>
                      <td className="px-3 py-2 text-right font-mono font-bold">${fmt(d.totales?.total)}</td>
                      <td className="px-3 py-2 text-center">
                        <div className="flex flex-col items-center gap-1">
                          <EstadoBadge estado={d.estado} label={d.estado_label} />
                          {d.emitido && d.tipo_dte !== '52' && <EstadoContableBadge estado={d.estado_contable} />}
                        </div>
                      </td>
                      <td className="px-3 py-2 text-center"><div className="flex flex-col items-center gap-0.5"><SituacionBadge situacion={d.situacion_comercial} />{d.nc_parcial && <span className="text-[9px] font-semibold text-amber-600" data-testid={`nc-parcial-${d.id}`}>con NC parcial</span>}</div></td>
                      <td className="px-3 py-2 text-muted-foreground">
                        {isPapelera ? (
                          <div>
                            <div>{fmtFecha(d.descartado_at)}</div>
                            <span className="text-[10px] text-amber-600">se elimina en {d.dias_para_purga ?? 5} día(s)</span>
                          </div>
                        ) : fmtFecha(d.created_at)}
                      </td>
                      <td className="px-3 py-2 text-center" onClick={(e) => e.stopPropagation()}>
                        {isPapelera ? (
                          <Button variant="outline" size="sm" className="h-7 px-2 text-[10px] text-emerald-700 border-emerald-300 hover:bg-emerald-50"
                            data-testid={`restaurar-${d.id}`} onClick={() => restaurar(d.id)}>
                            <RotateCcw className="w-3 h-3 mr-0.5" /> Restaurar
                          </Button>
                        ) : (
                          <Button variant="outline" size="sm" className="h-7 px-2 text-[10px]" data-testid={`ver-emision-${d.id}`} onClick={() => navigate(`/emision-dte/${d.id}`)}>
                            <Eye className="w-3 h-3 mr-0.5" /> Ver
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialog Nuevo documento */}
      <Dialog open={nuevoOpen} onOpenChange={(o) => !creando && setNuevoOpen(o)}>
        <DialogContent className="max-w-lg" data-testid="dialog-nuevo-documento">
          <DialogHeader>
            <DialogTitle>Nuevo documento de venta</DialogTitle>
            <DialogDescription>Crea un borrador. Luego podrás validar, previsualizar y emitir.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex gap-1.5 p-1 bg-muted rounded-lg">
              <button type="button" onClick={() => setModo('nuevo')} data-testid="modo-nuevo"
                className={`flex-1 text-xs font-medium py-1.5 rounded-md transition-all ${modo === 'nuevo' ? 'bg-white shadow text-indigo-700' : 'text-muted-foreground'}`}>
                Documento nuevo
              </button>
              <button type="button" onClick={() => setModo('proforma')} data-testid="modo-proforma"
                className={`flex-1 text-xs font-medium py-1.5 rounded-md transition-all ${modo === 'proforma' ? 'bg-white shadow text-indigo-700' : 'text-muted-foreground'}`}>
                Desde proforma aprobada
              </button>
            </div>
            <div>
              <Label className="text-xs">Tipo de documento</Label>
              <select className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm" value={nuevoForm.tipo_dte}
                onChange={(e) => setNuevoForm({ ...nuevoForm, tipo_dte: e.target.value })} data-testid="nuevo-tipo-dte">
                <option value="33">Factura Afecta (33)</option>
                <option value="34">Factura Exenta (34)</option>
                <option value="52">Guía de Despacho (52)</option>
                <option value="61">Nota de Crédito (61)</option>
                <option value="56">Nota de Débito (56)</option>
              </select>
            </div>
            {modo === 'proforma' ? (
              <div>
                <Label className="text-xs">Proforma aprobada</Label>
                <SearchableCombobox
                  items={proformas.map((p) => ({ value: p.id, label: `#${p.numero_proforma} · ${p.cliente_nombre || ''}`, subLabel: `$${Number(p.total || p.totales?.total || 0).toLocaleString('es-CL')}` }))}
                  value={proformaId}
                  onChange={(it) => setProformaId(it ? it.value : '')}
                  placeholder="Buscar proforma aprobada…"
                  testid="nuevo-proforma-combo"
                />
                <p className="text-[11px] text-muted-foreground mt-1">Se copian cliente, ítems y referencias de la proforma. Luego los podrás ajustar.</p>
                {proformas.length === 0 && <p className="text-[11px] text-amber-600 mt-1">No hay proformas aprobadas disponibles.</p>}
              </div>
            ) : (
              <>
                <div>
                  <Label className="text-xs">Cliente</Label>
                  <SearchableCombobox
                    items={clientes.map((c) => ({ value: c.rut, label: c.razon_social, subLabel: c.rut }))}
                    value={nuevoForm.cliente_rut}
                    onChange={(it) => setNuevoForm({ ...nuevoForm, cliente_rut: it ? it.value : '' })}
                    placeholder="Buscar cliente por nombre o RUT…"
                    testid="nuevo-cliente-combo"
                  />
                </div>
                <div>
                  <Label className="text-xs">N° Orden de Compra del cliente (opcional)</Label>
                  <Input value={nuevoForm.orden_compra} onChange={(e) => setNuevoForm({ ...nuevoForm, orden_compra: e.target.value })} data-testid="nuevo-oc" />
                </div>
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNuevoOpen(false)} disabled={creando}>Cancelar</Button>
            <Button onClick={crearDoc} disabled={creando || (modo === 'nuevo' ? !nuevoForm.cliente_rut : !proformaId)} className="bg-indigo-600 hover:bg-indigo-700" data-testid="btn-crear-documento">
              {creando ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />} Crear borrador
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Folios */}
      {/* Traer documento externo (DTECore) */}
      <Dialog open={externoOpen} onOpenChange={(o) => !traendo && setExternoOpen(o)}>
        <DialogContent data-testid="dialog-traer-externo">
          <DialogHeader>
            <DialogTitle>Traer documento externo</DialogTitle>
            <DialogDescription>Incorpora al ERP un documento emitido directamente en DTECore, indicando su tipo y folio. Quedará como Externo (solo lectura).</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Tipo de documento</Label>
              <select className="w-full border rounded-md h-9 px-2 text-sm mt-1" data-testid="externo-tipo"
                value={externoForm.tipo_dte} onChange={(e) => setExternoForm({ ...externoForm, tipo_dte: e.target.value })}>
                <option value="33">Factura Afecta (33)</option>
                <option value="34">Factura Exenta (34)</option>
                <option value="61">Nota de Crédito (61)</option>
                <option value="52">Guía de Despacho (52)</option>
                <option value="56">Nota de Débito (56)</option>
              </select>
            </div>
            <div>
              <Label>Folio</Label>
              <Input type="number" className="mt-1" placeholder="Ej: 2784" data-testid="externo-folio"
                value={externoForm.folio} onChange={(e) => setExternoForm({ ...externoForm, folio: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setExternoOpen(false)} disabled={traendo}>Cancelar</Button>
            <Button onClick={traerExterno} disabled={traendo} data-testid="btn-confirmar-traer-externo" className="bg-amber-600 hover:bg-amber-700">
              {traendo ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <CloudDownload className="w-4 h-4 mr-1" />} Traer al ERP
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={foliosOpen} onOpenChange={(o) => !savingFolios && setFoliosOpen(o)}>
        <DialogContent className="max-w-xl" data-testid="dialog-folios">
          <DialogHeader>
            <DialogTitle>Folios disponibles (CAF)</DialogTitle>
            <DialogDescription>Conteos configurados manualmente (Fase 1). En Fase 2 se consultan en vivo a DTECore.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {['33', '34', '52', '61', '56'].map((tipo) => {
              const f = foliosForm[tipo] || {};
              return (
                <div key={tipo} className="rounded-lg border p-3 grid grid-cols-4 gap-2 items-end" data-testid={`folios-row-${tipo}`}>
                  <div className="col-span-4 text-sm font-semibold">{TIPO_LABEL[tipo]} ({tipo})</div>
                  <div><Label className="text-[10px]">Disponibles</Label>
                    <Input type="number" value={f.disponibles ?? 0} onChange={(e) => setFoliosForm({ ...foliosForm, [tipo]: { ...f, disponibles: e.target.value } })} data-testid={`folios-disp-${tipo}`} /></div>
                  <div><Label className="text-[10px]">Alerta ≤</Label>
                    <Input type="number" value={f.umbral_alerta ?? 20} onChange={(e) => setFoliosForm({ ...foliosForm, [tipo]: { ...f, umbral_alerta: e.target.value } })} /></div>
                  <div><Label className="text-[10px]">Próx. folio</Label>
                    <Input type="number" value={f.proximo_folio ?? ''} onChange={(e) => setFoliosForm({ ...foliosForm, [tipo]: { ...f, proximo_folio: e.target.value } })} /></div>
                  <label className="flex items-center gap-1 text-[11px] pb-2">
                    <input type="checkbox" checked={f.bloqueo_sin_folios !== false} onChange={(e) => setFoliosForm({ ...foliosForm, [tipo]: { ...f, bloqueo_sin_folios: e.target.checked } })} />
                    Bloquear
                  </label>
                </div>
              );
            })}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFoliosOpen(false)} disabled={savingFolios}>Cancelar</Button>
            <Button onClick={guardarFolios} disabled={savingFolios} data-testid="btn-guardar-folios">
              {savingFolios ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null} Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Conexión proveedor */}
      <Dialog open={cfgOpen} onOpenChange={(o) => !savingCfg && setCfgOpen(o)}>
        <DialogContent className="max-w-lg" data-testid="dialog-conexion">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Cloud className="w-5 h-5 text-purple-600" /> Conexión con el proveedor (SII Solutions)</DialogTitle>
            <DialogDescription>Elige cómo se emiten los documentos. Empieza en Simulación; Producción emite documentos reales ante el SII.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex items-center justify-between rounded-md border p-2.5 text-sm">
              <span className="text-muted-foreground">Credenciales del proveedor</span>
              {config?.proveedor_credenciales
                ? <span className="text-emerald-600 font-medium flex items-center gap-1"><CheckCircle2 className="w-4 h-4" /> Configuradas</span>
                : <span className="text-amber-600 font-medium flex items-center gap-1"><AlertTriangle className="w-4 h-4" /> Faltan (pídelas a soporte)</span>}
            </div>
            <div className="space-y-2">
              <Label className="text-xs">Modo de emisión</Label>
              {[
                { v: 'registro', t: 'Registro interno', d: 'No llama al proveedor. Deja el documento registrado en el ERP (Fase 1).' },
                { v: 'simulacion', t: 'Simulación (proveedor)', d: 'Llama al proveedor pero NO envía al SII. Ideal para probar sin riesgo.' },
                { v: 'produccion', t: 'Producción (real)', d: 'Emite de verdad ante el SII. Úsalo solo cuando esté todo listo.' },
              ].map((opt) => (
                <label key={opt.v} data-testid={`modo-cfg-${opt.v}`}
                  className={`flex items-start gap-2 rounded-md border p-2.5 cursor-pointer transition-all ${cfgForm.modo === opt.v ? 'border-purple-400 bg-purple-50/50' : 'hover:bg-muted/40'}`}>
                  <input type="radio" name="modo" className="mt-1" checked={cfgForm.modo === opt.v}
                    onChange={() => setCfgForm({ ...cfgForm, modo: opt.v, confirmar_produccion: false })} />
                  <div>
                    <p className="text-sm font-medium flex items-center gap-1">{opt.t}{opt.v === 'produccion' && <ShieldAlert className="w-3.5 h-3.5 text-red-500" />}</p>
                    <p className="text-[11px] text-muted-foreground">{opt.d}</p>
                  </div>
                </label>
              ))}
            </div>
            {cfgForm.modo === 'produccion' && (
              <label className="flex items-start gap-2 text-xs bg-red-50 border border-red-200 rounded-md p-2.5">
                <input type="checkbox" className="mt-0.5" checked={cfgForm.confirmar_produccion} data-testid="cfg-confirmar-produccion"
                  onChange={(e) => setCfgForm({ ...cfgForm, confirmar_produccion: e.target.checked })} />
                <span>Confirmo que quiero emitir <b>documentos reales ante el SII</b>. El proveedor firmará, tomará folio y los enviará.</span>
              </label>
            )}
            <div className="grid grid-cols-3 gap-2">
              <div className="col-span-1"><Label className="text-[10px]">RUT emisor</Label><Input className="h-9" value={cfgForm.emisor_rut || ''} onChange={(e) => setCfgForm({ ...cfgForm, emisor_rut: e.target.value })} data-testid="cfg-emisor-rut" /></div>
              <div className="col-span-1"><Label className="text-[10px]">Sucursal</Label><Input className="h-9" value={cfgForm.sucursal_nombre || ''} onChange={(e) => setCfgForm({ ...cfgForm, sucursal_nombre: e.target.value })} /></div>
              <div className="col-span-1"><Label className="text-[10px]">Cód. sucursal</Label><Input className="h-9" value={cfgForm.sucursal_codigo || ''} onChange={(e) => setCfgForm({ ...cfgForm, sucursal_codigo: e.target.value })} /></div>
            </div>
            <div className="rounded-md border p-2.5">
              <Button variant="outline" size="sm" onClick={probarConexion} disabled={probando || !config?.proveedor_credenciales} data-testid="btn-probar-conexion">
                {probando ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Cloud className="w-4 h-4 mr-1" />} Probar conexión (no emite)
              </Button>
              {probeResult && (
                <div className={`mt-2 text-xs flex items-start gap-1.5 ${probeResult.ok ? 'text-emerald-700' : 'text-red-700'}`} data-testid="probe-result">
                  {probeResult.ok ? <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" /> : <XCircle className="w-4 h-4 shrink-0 mt-0.5" />}
                  <span>{probeResult.mensaje}</span>
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCfgOpen(false)} disabled={savingCfg}>Cancelar</Button>
            <Button onClick={guardarConfig} disabled={savingCfg || (cfgForm.modo !== 'registro' && !config?.proveedor_credenciales)} className="bg-purple-600 hover:bg-purple-700" data-testid="btn-guardar-conexion">
              {savingCfg ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null} Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog confirmar limpieza de borradores */}
      <Dialog open={limpiarOpen} onOpenChange={(o) => !limpiando && setLimpiarOpen(o)}>
        <DialogContent className="max-w-md" data-testid="dialog-limpiar">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Trash2 className="w-5 h-5 text-red-600" /> Limpiar borradores</DialogTitle>
            <DialogDescription>
              Se enviarán <b>{selected.size}</b> borrador(es) a la papelera. Podrás restaurarlos durante <b>5 días</b>; después se eliminan automáticamente. Los documentos ya emitidos nunca se tocan.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLimpiarOpen(false)} disabled={limpiando}>Cancelar</Button>
            <Button className="bg-red-600 hover:bg-red-700" onClick={confirmarLimpiar} disabled={limpiando} data-testid="btn-confirmar-limpiar">
              {limpiando ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Trash2 className="w-4 h-4 mr-1" />} Enviar a papelera
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ModoBadge({ config }) {
  const map = {
    registro: { t: 'Registro interno', cls: 'bg-gray-100 text-gray-700 border-gray-200' },
    simulacion: { t: 'Simulación (proveedor)', cls: 'bg-amber-100 text-amber-800 border-amber-200' },
    produccion: { t: 'Producción real (SII)', cls: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
  };
  const m = map[config.modo] || map.registro;
  return (
    <span data-testid="modo-emision-badge" className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] font-semibold ${m.cls}`}>
      <Cloud className="w-3 h-3" /> {m.t}
    </span>
  );
}
